import styled, { css } from 'styled-components';
import { EditorialContainer } from '../../components/editorial/PublicationShared';
import { gradientPillCss } from '../../styles/gradientPill';

export const PageShell = styled.main`
  min-height: 100vh;
  background:
    radial-gradient(
      1100px 640px at 16% 4%,
      rgba(141, 235, 255, 0.24) 0%,
      rgba(141, 235, 255, 0) 60%
    ),
    radial-gradient(
      960px 620px at 84% 18%,
      rgba(248, 201, 165, 0.22) 0%,
      rgba(248, 201, 165, 0) 58%
    ),
    linear-gradient(180deg, #efecf0 0%, #efecf0 100%);
  color: #17212f;
`;

export const ContentContainer = styled(EditorialContainer)`
  position: relative;
  z-index: 1;
`;

export const HeroSectionShell = styled.section`
  padding: 0 0 clamp(4.5rem, 8vw, 6.5rem);
`;

export const Section = styled.section<{ $border?: boolean; $emphasis?: boolean }>`
  scroll-margin-top: 112px;
  padding: clamp(3.6rem, 6vw, 5.5rem) 0;
  border-top: ${({ $border = true }) =>
    $border ? '1px solid rgba(23, 33, 47, 0.1)' : '0'};
  background: ${({ $emphasis }) =>
    $emphasis
      ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.38), rgba(255, 255, 255, 0.1))'
      : 'transparent'};
`;

export const SectionIntro = styled.div<{ $wide?: boolean; $center?: boolean }>`
  display: grid;
  gap: 0.9rem;
  max-width: ${({ $wide }) => ($wide ? '56rem' : '42rem')};
  margin: ${({ $center }) => ($center ? '0 auto' : '0')};
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
`;

export const SectionEyebrow = styled.p`
  margin: 0;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #8c5b24;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(2.1rem, 4.2vw, 3.9rem);
  line-height: 0.95;
  letter-spacing: -0.05em;
  color: #101828;
  text-wrap: balance;
`;

export const SectionLead = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.78;
  color: rgba(23, 33, 47, 0.78);
`;

export const SectionTextStack = styled.div`
  display: grid;
  gap: 1rem;
`;

export const SectionParagraph = styled.p`
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.78;
  color: rgba(23, 33, 47, 0.82);
`;

export const HeroStack = styled.div`
  display: grid;
  gap: 1.2rem;
  justify-items: center;
  text-align: center;
  max-width: 58rem;
  margin: 0 auto;
`;

export const HeroEyebrow = styled(SectionEyebrow)`
  margin-bottom: 0.15rem;
`;

export const HeroTitle = styled.h1`
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(3.2rem, 7vw, 6.4rem);
  line-height: 0.92;
  letter-spacing: -0.065em;
  color: #111827;
  text-wrap: balance;
`;

export const HeroBody = styled.p`
  margin: 0;
  max-width: 48rem;
  font-size: 1.08rem;
  line-height: 1.86;
  color: rgba(23, 33, 47, 0.8);
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.85rem;
  margin-top: 0.35rem;
`;

const anchorReset = css`
  text-decoration: none;
`;

export const PrimaryAction = styled.a`
  ${gradientPillCss};
  ${anchorReset};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const SplitGrid = styled.div<{ $reverse?: boolean }>`
  display: grid;
  gap: clamp(1.25rem, 3vw, 2rem);
  grid-template-columns: ${({ $reverse }) =>
    $reverse ? 'minmax(0, 0.92fr) minmax(0, 1.08fr)' : 'minmax(0, 1.08fr) minmax(0, 0.92fr)'};
  align-items: start;
  margin-top: 1.75rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const CardStack = styled.div`
  display: grid;
  gap: 1rem;
`;

export const SurfaceCard = styled.article`
  position: relative;
  border-radius: 24px;
  padding: 1.35rem;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.84), rgba(255, 255, 255, 0.68));
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow:
    0 20px 44px rgba(15, 23, 40, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.65);
`;

export const SurfaceEyebrow = styled.p`
  margin: 0 0 0.6rem;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8c5b24;
`;

export const SurfaceTitle = styled.h3`
  margin: 0;
  font-size: 1.14rem;
  line-height: 1.2;
  color: #111827;
`;

export const SurfaceText = styled.p`
  margin: 0.72rem 0 0;
  font-size: 0.94rem;
  line-height: 1.65;
  color: rgba(23, 33, 47, 0.78);
`;

export const HighlightNote = styled.div`
  border-radius: 18px;
  padding: 1rem 1.05rem;
  background: rgba(23, 33, 47, 0.05);
  border: 1px solid rgba(23, 33, 47, 0.08);
  color: rgba(23, 33, 47, 0.8);
  font-size: 0.92rem;
  line-height: 1.6;
`;

export const ProofStrip = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 1.5rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const ProofCard = styled(SurfaceCard)`
  min-height: 100%;
`;

export const CardGrid = styled.div<{ $columns?: number }>`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, minmax(0, 1fr));
  margin-top: 1.5rem;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  margin-top: 1rem;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.48rem 0.82rem;
  background: rgba(23, 33, 47, 0.05);
  border: 1px solid rgba(23, 33, 47, 0.08);
  color: rgba(23, 33, 47, 0.82);
  font-size: 0.84rem;
  font-weight: 700;
`;

export const StepGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 1.7rem;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const StepCard = styled(SurfaceCard)`
  display: grid;
  gap: 0.8rem;
`;

export const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  background: rgba(224, 161, 94, 0.12);
  border: 1px solid rgba(224, 161, 94, 0.28);
  color: #8c5b24;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.08em;
`;

export const QualificationList = styled.ul`
  margin: 1rem 0 0;
  padding-left: 1.1rem;
  display: grid;
  gap: 0.65rem;
`;

export const QualificationItem = styled.li`
  color: rgba(23, 33, 47, 0.8);
  font-size: 0.95rem;
  line-height: 1.6;
`;

export const ApplicationLayout = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  margin-top: 1.7rem;
`;

export const FormAside = styled.div`
  display: grid;
  gap: 1rem;
  align-self: start;
`;

export const FormShell = styled(SurfaceCard)`
  padding: 1.25rem;
`;

export const Form = styled.form`
  display: grid;
  gap: 0.9rem;
`;

export const FieldGrid = styled.div`
  display: grid;
  gap: 0.8rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.label`
  display: grid;
  gap: 0.35rem;
`;

export const FieldLabel = styled.span`
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: rgba(23, 33, 47, 0.58);
`;

const glassFieldCss = css`
  border: 1px solid rgba(23, 33, 47, 0.12);
  background: rgba(255, 255, 255, 0.32);
  color: #17212f;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.56),
    0 10px 22px rgba(23, 33, 47, 0.04);
  backdrop-filter: blur(14px);

  &::placeholder {
    color: #7b8794;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, 0.72);
    background: rgba(255, 255, 255, 0.4);
  }
`;

export const TextInput = styled.input`
  min-height: 42px;
  border-radius: 12px;
  padding: 0 0.9rem;
  font-size: 0.94rem;
  ${glassFieldCss};
`;

export const SelectInput = styled.select`
  min-height: 42px;
  border-radius: 12px;
  padding: 0 0.9rem;
  font-size: 0.94rem;
  ${glassFieldCss};

  option {
    color: #17212f;
    background: #efecf0;
  }
`;

export const TextArea = styled.textarea`
  min-height: 132px;
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
  resize: vertical;
  font-size: 0.94rem;
  font-family: inherit;
  ${glassFieldCss};
`;

export const FormHelper = styled.p`
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.55;
  color: rgba(23, 33, 47, 0.66);
`;

export const FormError = styled.p`
  margin: 0;
  font-size: 0.84rem;
  font-weight: 800;
  color: #a63b32;
`;

export const SubmitButton = styled.button`
  ${gradientPillCss};
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
`;

export const LegalNote = styled.p`
  margin: 1rem 0 0;
  font-size: 0.78rem;
  line-height: 1.6;
  color: rgba(23, 33, 47, 0.62);
`;
